import React from 'react';
import { useTranslation } from '../../i18n';
import { TeamId, TeamsState } from '../../types/teams';
import { AppIcon } from './icons';
import '../../styles/lobby.css';

interface TeamPlayer {
  id: string;
  name: string;
  avatar?: string;
  teamId?: TeamId;
}

interface TeamAssignPanelProps {
  players: TeamPlayer[];
  teams?: TeamsState;
  /** Host can assign anyone; non-host only self when myPlayerId is set */
  canAssignAny: boolean;
  myPlayerId?: string | null;
  onSetTeam: (playerId: string, teamId: TeamId | null) => void;
  className?: string;
}

function PlayerAvatar({ avatar }: { avatar?: string }) {
  if (avatar && (avatar.startsWith('/') || avatar.startsWith('data:') || avatar.includes('.svg'))) {
    return <img src={avatar} alt="" className="team-assign-avatar-img" />;
  }
  return <span className="team-assign-avatar-emoji">{avatar || '👤'}</span>;
}

export const TeamAssignPanel: React.FC<TeamAssignPanelProps> = ({
  players,
  teams,
  canAssignAny,
  myPlayerId,
  onSetTeam,
  className,
}) => {
  const { t } = useTranslation();
  const nameA = teams?.A?.name || t('lobby.teams.teamA');
  const nameB = teams?.B?.name || t('lobby.teams.teamB');

  const teamA = players.filter((p) => p.teamId === 'A');
  const teamB = players.filter((p) => p.teamId === 'B');
  const unassigned = players.filter((p) => !p.teamId);

  const canEdit = (playerId: string) => canAssignAny || playerId === myPlayerId;

  const renderPlayer = (player: TeamPlayer) => {
    const editable = canEdit(player.id);
    return (
      <div key={player.id} className="team-assign-player">
        <div className="team-assign-player-info">
          <PlayerAvatar avatar={player.avatar} />
          <span className="team-assign-player-name">{player.name}</span>
        </div>
        {editable && (
          <div className="team-assign-player-actions">
            <button
              type="button"
              className={`team-assign-chip ${player.teamId === 'A' ? 'team-assign-chip--active team-assign-chip--a' : ''}`}
              onClick={() => onSetTeam(player.id, player.teamId === 'A' ? null : 'A')}
              aria-pressed={player.teamId === 'A'}
            >
              A
            </button>
            <button
              type="button"
              className={`team-assign-chip ${player.teamId === 'B' ? 'team-assign-chip--active team-assign-chip--b' : ''}`}
              onClick={() => onSetTeam(player.id, player.teamId === 'B' ? null : 'B')}
              aria-pressed={player.teamId === 'B'}
            >
              B
            </button>
          </div>
        )}
        {!editable && player.teamId && (
          <span className={`team-assign-badge team-assign-badge--${player.teamId.toLowerCase()}`}>
            {player.teamId}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className={`team-assign-panel ${className || ''}`}>
      <h3 className="player-list-section-title">
        <AppIcon name="people" fontSize="small" /> {t('lobby.teams.title')}
      </h3>
      <div className="team-assign-columns">
        <div className="team-assign-column team-assign-column--a">
          <div className="team-assign-column-header">
            <span>{nameA}</span>
            <span className="team-assign-count">{teamA.length}</span>
          </div>
          <div className="team-assign-column-body">
            {teamA.length === 0 ? (
              <p className="team-assign-empty">{t('lobby.teams.empty')}</p>
            ) : (
              teamA.map(renderPlayer)
            )}
          </div>
        </div>
        <div className="team-assign-column team-assign-column--b">
          <div className="team-assign-column-header">
            <span>{nameB}</span>
            <span className="team-assign-count">{teamB.length}</span>
          </div>
          <div className="team-assign-column-body">
            {teamB.length === 0 ? (
              <p className="team-assign-empty">{t('lobby.teams.empty')}</p>
            ) : (
              teamB.map(renderPlayer)
            )}
          </div>
        </div>
      </div>
      {unassigned.length > 0 && (
        <div className="team-assign-unassigned">
          <div className="team-assign-column-header">
            <span>{t('lobby.teams.unassigned')}</span>
            <span className="team-assign-count">{unassigned.length}</span>
          </div>
          <div className="team-assign-column-body team-assign-column-body--row">
            {unassigned.map(renderPlayer)}
          </div>
        </div>
      )}
      {!canAssignAny && (
        <p className="team-assign-hint">{t('lobby.teams.pickHint')}</p>
      )}
    </div>
  );
};
